import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { ExternalLink } from "lucide-react";

const TABS = [
  { k: "all", l: "All" },
  { k: "image", l: "Photos" },
  { k: "video", l: "Highlights" },
];

export default function Media() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((i) => i.type === filter);
  }, [items, filter]);

  return (
    <div data-testid="media-page" className="text-white">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24">
        <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
          Media
        </div>
        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl">
          Gallery & <span className="text-axe-purple">Highlights</span>
        </h1>
        <p className="mt-5 max-w-2xl text-neutral-400 text-base sm:text-lg">
          Snapshots from tournaments, clips from our top plays, and behind-the-scenes moments from
          the AXE house.
        </p>

        <div className="mt-10 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              data-testid={`media-filter-${t.k}`}
              className={`px-5 py-2 rounded-full text-[11px] uppercase tracking-[0.25em] font-bold border transition-colors ${
                filter === t.k
                  ? "bg-axe-purple border-axe-purple text-white drop-shadow-[0_0_14px_rgba(176,38,255,0.45)]"
                  : "bg-axe-surface border-axe-border text-neutral-400 hover:text-white"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-10">
        {loading ? (
          <div className="text-neutral-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-neutral-500 border border-axe-border rounded-2xl p-8 text-center" data-testid="media-empty">
            Nothing in this bucket yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((i) => (
              <div
                key={i.id}
                data-testid={`media-item-${i.id}`}
                className="group rounded-2xl overflow-hidden border border-axe-border bg-axe-surface hover:border-axe-purple/60 transition-all"
              >
                {i.type === "image" ? (
                  <button
                    type="button"
                    className="block w-full aspect-[4/3] relative overflow-hidden"
                    onClick={() => setLightbox(i)}
                  >
                    <img src={i.url} alt={i.caption || ""} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-axe-surface/90 via-transparent to-transparent" />
                    {i.caption && (
                      <div className="absolute inset-x-0 bottom-0 p-4 text-left text-sm font-semibold text-white">
                        {i.caption}
                      </div>
                    )}
                  </button>
                ) : (
                  <div>
                    <div className="relative aspect-video bg-black">
                      <iframe
                        title={i.caption || "Highlight"}
                        src={i.url}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                      />
                    </div>
                    <div className="p-4 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-axe-tangerine font-bold">
                          {i.platform || "Clip"}
                        </div>
                        <div className="mt-1 font-display font-semibold text-base text-white truncate">
                          {i.caption || "Highlight"}
                        </div>
                      </div>
                      <a href={i.url} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-axe-purple" aria-label="Open in new tab">
                        <ExternalLink size={16} />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {lightbox && (
        <button
          type="button"
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="media-lightbox"
          aria-label="Close"
        >
          <img src={lightbox.url} alt={lightbox.caption || ""} className="max-h-full max-w-full object-contain rounded-xl" />
        </button>
      )}
    </div>
  );
}
